/**
 * ADR 370 — the blocker mask (ADR 343) as a collider for the walk camera.
 *
 * One bit per cell in the mask's own frame: `originX/Y` is the low corner in
 * warehouse metres, cell = floor((x − origin) / cellM), row 0 = the lowest
 * world Y. The composed PNG the server sends is exactly that grid, row 0 at the
 * top of the image (Java2D `setRGB(x, y)` with y = the cell row), white =
 * blocked. In the scene the warehouse (X, Y) plane is three.js (x, z) — ADR 25 —
 * so `blockedAt(x, z)` takes scene coordinates.
 */
import type { BlockerMaskMeta } from "../api/rest";

/** Shoulder half-width of the walker. Cells are 0,25 m, so this samples ~3 cells across. */
export const WALKER_RADIUS_M = 0.35;

export class BlockerField {
  constructor(
    readonly originX: number,
    readonly originY: number,
    readonly cellM: number,
    readonly widthPx: number,
    readonly heightPx: number,
    /** One byte per cell, row-major; non-zero = blocked. */
    readonly cells: Uint8Array,
  ) {}

  /** From the decoded PNG's RGBA pixels (ImageData.data), row 0 = cell row 0. */
  static fromPixels(meta: BlockerMaskMeta, rgba: Uint8ClampedArray | Uint8Array): BlockerField {
    const n = meta.widthPx * meta.heightPx;
    if (rgba.length < n * 4) throw new Error(`blocker mask: ${rgba.length / 4} pixels for a ${meta.widthPx}x${meta.heightPx} frame`);
    const cells = new Uint8Array(n);
    for (let i = 0; i < n; i++) cells[i] = rgba[i * 4] > 127 ? 1 : 0;
    return new BlockerField(meta.originX, meta.originY, meta.cellM, meta.widthPx, meta.heightPx, cells);
  }

  cellOf(x: number, z: number): [number, number] {
    return [Math.floor((x - this.originX) / this.cellM), Math.floor((z - this.originY) / this.cellM)];
  }

  blockedAt(x: number, z: number): boolean {
    const [c, r] = this.cellOf(x, z);
    if (c < 0 || r < 0 || c >= this.widthPx || r >= this.heightPx) return false;   // off the frame = open floor
    return this.cells[r * this.widthPx + c] !== 0;
  }

  /** A disc of `radius` around (x, z) touches a blocked cell. */
  bodyBlocked(x: number, z: number, radius = WALKER_RADIUS_M): boolean {
    if (this.blockedAt(x, z)) return true;
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      if (this.blockedAt(x + Math.cos(a) * radius, z + Math.sin(a) * radius)) return true;
    }
    return false;
  }

  count(): number {
    let n = 0;
    for (let i = 0; i < this.cells.length; i++) n += this.cells[i];
    return n;
  }
}

/**
 * Where a step from (fx, fz) toward (tx, tz) actually lands. The step is SWEPT
 * in sub-steps of half a cell, so a running walker (10 m/s, 100 ms frames = a
 * metre a frame) cannot jump a half-metre wall by landing beyond it. At each
 * sub-step: go through when clear; otherwise slide along the obstacle (x-only,
 * then z-only); otherwise stop there. A walker already inside a blocker (walk
 * mode entered on top of a rack) is never trapped: while the body overlaps a
 * blocker every move is allowed, so it can step out.
 */
export function resolveMove(
  field: BlockerField, fx: number, fz: number, tx: number, tz: number, radius = WALKER_RADIUS_M,
): [number, number] {
  const dx = tx - fx, dz = tz - fz;
  const dist = Math.hypot(dx, dz);
  if (dist === 0) return [fx, fz];
  const n = Math.max(1, Math.ceil(dist / (field.cellM / 2)));
  let x = fx, z = fz;
  for (let k = 1; k <= n; k++) {
    const nx = fx + (dx * k) / n, nz = fz + (dz * k) / n;
    if (field.bodyBlocked(x, z, radius)) { x = nx; z = nz; continue; }        // escape first
    if (!field.bodyBlocked(nx, nz, radius)) { x = nx; z = nz; continue; }
    const sx = x + dx / n, sz = z + dz / n;                                     // slide: one component at a time
    if (dx !== 0 && !field.bodyBlocked(sx, z, radius)) { x = sx; continue; }
    if (dz !== 0 && !field.bodyBlocked(x, sz, radius)) { z = sz; continue; }
    break;
  }
  return [x, z];
}
